import axios from 'axios';
import * as Sentry from '@sentry/react';
import { API_URL } from '@/utils/constants';
import { refineErrorMessage } from '@/utils/errorMessages';
import { triggerGlobalToast } from '@/contexts/ToastContext';

// Create axios instance
export const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    // Get token from localStorage (will be set by Privy)
    const token = localStorage.getItem('authToken');

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling with smart error discrimination
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const errorCode = error.response?.data?.code;
    const errorMessage = error.response?.data?.error || '';

    // Only clear auth and redirect on ACTUAL authentication failures
    if (status === 401) {
      // These error codes indicate real auth problems (invalid/missing token, user deleted)
      const authFailureCodes = ['AUTH_NO_TOKEN', 'AUTH_INVALID_TOKEN', 'AUTH_USER_NOT_FOUND'];

      if (authFailureCodes.includes(errorCode)) {
        console.warn('[API Client] Authentication failed:', errorCode);
        localStorage.removeItem('authToken');
        triggerGlobalToast(refineErrorMessage(error), 'error');
        window.location.href = '/';
        return Promise.reject(error);
      }

      // If it's a 401 without a known auth code, it might be a service issue
      console.warn('[API Client] 401 error but not an auth failure:', errorCode || errorMessage);
    }

    // Handle 503 Service Unavailable gracefully (don't logout)
    if (status === 503) {
      console.warn('[API Client] Service temporarily unavailable:', errorMessage);
    }

    // For network errors (no response at all)
    if (!error.response) {
      console.error('[API Client] Network error - no response from server');
    }

    // Capture API errors in Sentry (except 401 auth errors which are expected)
    if (status !== 401) {
      Sentry.withScope((scope) => {
        scope.setTag('api.url', error.config?.url || 'unknown');
        scope.setTag('api.method', error.config?.method || 'unknown');
        scope.setTag('api.status', status || 'network_error');
        scope.setExtra('response', error.response?.data);
        Sentry.captureException(error);
      });
    }

    // Show user-friendly error toast for all errors
    triggerGlobalToast(refineErrorMessage(error), 'error');

    return Promise.reject(error);
  }
);
